import Vue from 'vue';
import { ListeningLedger, listeningDay, type ListeningDays } from '../listening-ledger';
const key = 'simple-music.listening.v1';
// Each document writes only its own partition; idle/concurrent tabs cannot overwrite another tab's records.
const ownKey = `${key}.tab.${crypto.randomUUID()}`;
const ledger = new ListeningLedger();
let foreignDays: ListeningDays = {};
let dirty = false;
export const listeningToday = Vue.observable({ seconds: 0, songs: 0, day: listeningDay(Date.now()), storageError: '' });
function loadForeign() {
  const merged: ListeningDays = {};
  try {
    for (let index = -1; index < localStorage.length; index++) {
      const item = index < 0 ? key : localStorage.key(index);
      if (!item || item === ownKey || item !== key && !item.startsWith(key + '.tab.')) continue;
      let saved;
      try { saved = JSON.parse(localStorage.getItem(item) || '{}'); } catch { continue; }
      const safe = new ListeningLedger(saved);
      for (const [day, tracks] of Object.entries(safe.days)) {
        const target = merged[day] ||= Object.create(null);
        for (const [id, seconds] of Object.entries(tracks)) target[id] = (target[id] || 0) + seconds;
      }
    }
    foreignDays = merged;
  } catch { listeningToday.storageError = '聆听记录无法读取，当前标签页仍继续统计。'; }
}
function refresh() {
  const day = listeningDay(Date.now());
  const tracks = { ...foreignDays[day] };
  for (const [id, seconds] of Object.entries(ledger.days[day] || {})) tracks[id] = (tracks[id] || 0) + seconds;
  Object.assign(listeningToday, { day, seconds: Object.values(tracks).reduce((sum, seconds) => sum + seconds, 0), songs: Object.values(tracks).filter(seconds => seconds >= 5).length });
}
loadForeign(); refresh();
export function trackListening(audio: HTMLAudioElement, currentId: () => string | null, current: () => boolean = () => true) {
  let lastSave = 0;
  let disposed = false;
  const subscriptions: (() => void)[] = [];
  const on = (target: EventTarget, type: string, listener: EventListener) => {
    target.addEventListener(type, listener);
    subscriptions.push(() => target.removeEventListener(type, listener));
  };
  const save = () => {
    if (!dirty) return;
    try { localStorage.setItem(ownKey, JSON.stringify(ledger.days)); dirty = false; listeningToday.storageError = ''; }
    catch { listeningToday.storageError = '聆听记录无法保存，当前标签页仍继续统计。'; }
    lastSave = performance.now();
  };
  const sample = () => {
    if (disposed || !current()) return;
    if (audio.seeking) { ledger.reset(); return; }
    if (ledger.sample({ trackId: currentId() || '', position: audio.currentTime, wall: Date.now(), monotonic: performance.now(), rate: audio.playbackRate, active: !audio.paused && !audio.ended && audio.readyState >= 2 })) dirty = true;
    refresh();
    if (dirty && performance.now() - lastSave >= 5000) save();
  };
  const finish = () => { if (disposed || !current()) return; sample(); ledger.reset(); save(); };
  on(audio, 'timeupdate', sample);
  on(audio, 'play', sample);
  on(audio, 'pause', finish);
  on(audio, 'ended', finish);
  for (const event of ['seeking', 'seeked', 'ratechange', 'emptied', 'waiting']) on(audio, event, () => { if (current()) ledger.reset(); });
  on(window, 'pagehide', finish);
  on(window, 'storage', value => { const event = value as StorageEvent; if (!event.key || event.key === key || event.key.startsWith(key + '.tab.')) { loadForeign(); refresh(); } });
  on(document, 'visibilitychange', () => { sample(); if (document.hidden) save(); });
  const refreshTimer = window.setInterval(refresh, 60000);
  return () => {
    if (disposed) return;
    if (current()) finish(); else save();
    disposed = true;
    for (const unsubscribe of subscriptions) unsubscribe();
    window.clearInterval(refreshTimer);
  };
}
